/**
 * Shared pieces for the JSON-LD structured data (round 5).
 * The business block itself is in public/index.html (sitewide, in the HTML
 * as first served). Pages link to it and to the Person on /about by @id.
 *
 * AHPRA advertising rules: no title words AHPRA restricts, and no review or
 * rating markup of any kind (tests/test_round5_seo_accuracy.py checks both).
 */
import { isValidElement } from 'react';

export const SITE = 'https://www.clinicalminds.net';
export const CLINIC_ID = `${SITE}/#clinic`;
export const PERSON_ID = `${SITE}/about#lachlan`;

export const AREA_SERVED = [
  { '@type': 'City', name: 'Coffs Harbour' },
  { '@type': 'City', name: 'Port Macquarie' },
  { '@type': 'City', name: 'Sydney' },
  { '@type': 'City', name: 'Melbourne' },
  { '@type': 'City', name: 'Gold Coast' },
  { '@type': 'City', name: 'Hamilton VIC' },
  { '@type': 'Country', name: 'Australia' },
];

export const PROVIDER = { '@type': 'MedicalClinic', '@id': CLINIC_ID, name: 'Clinical Minds Psychology' };

/**
 * The visible text of an answer, whether it is a plain string or JSX with
 * links, so the FAQ data always matches exactly what people read.
 */
export function nodeToText(node) {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (isValidElement(node)) return nodeToText(node.props.children);
  return '';
}

export function faqPageSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: nodeToText(f.answer).replace(/\s+/g, ' ').trim() },
    })),
  };
}

export function serviceSchema({ name, description, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    serviceType: name,
    description,
    url,
    provider: PROVIDER,
    areaServed: AREA_SERVED,
  };
}

// /about: Lachlan, as he described himself (round 5 brief, verbatim facts only)
export const lachlanPerson = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': PERSON_ID,
  name: 'Lachlan Beaton',
  jobTitle: 'Clinical Psychologist (Registrar)',
  url: `${SITE}/about`,
  image: `${SITE}/images/photos/lachlan-headshot.jpg`,
  worksFor: PROVIDER,
  identifier: { '@type': 'PropertyValue', propertyID: 'AHPRA', value: 'PSY0002785698' },
  alumniOf: [
    { '@type': 'CollegeOrUniversity', name: 'University of New England' },
    { '@type': 'CollegeOrUniversity', name: 'Australian Catholic University' },
    { '@type': 'CollegeOrUniversity', name: 'The University of Melbourne' },
  ],
  knowsAbout: [
    'autism assessment',
    'ADHD assessment',
    'FASD assessment',
    'specific learning disorder',
    'intellectual disability',
    'neurodiversity',
    'addiction',
  ],
  sameAs: ['https://doi.org/10.1080/00918369.2025.2540370', 'https://doi.org/10.1002/capr.70114'],
};
